import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container } from "react-bootstrap";
import styles from "./ModernHeader.module.css";
import tabStyles from "../UI/Tabs.module.css";
import UserProfileMenu from './UserProfileMenu';
import useTheme from '../../../hooks/useTheme';
import ThemeToggle from './ThemeToggle';
import { useNavActiveSection } from './NavActiveSectionContext';
import FeedbackButton from '../Feedback/FeedbackButton';
import FeedbackModal from '../Feedback/FeedbackModal';
import useFeedbackSubmit from '../../../hooks/useFeedbackSubmit';

/**
 * ModernHeader - Professional Cox-branded header component
 * Features Cox dark blue background with white branding
 *
 * @param {Object} props
 * @param {Object} props.user - User object for future user menu integration
 */
const ModernHeader = ({ user }) => {
  const { theme, toggleTheme } = useTheme();

  // Data-driven nav highlight (Approach A).
  // A detail page (which matches neither NavLink) can publish the section it
  // belongs to; we force the corresponding tab's active styling. This is a
  // VISUAL-ONLY highlight (Decision A): we apply only the active CSS class and
  // emit NO aria-current here. On / and /past-incidents `section` is null and
  // NavLink's own URL-based active state (incl. aria-current="page") applies.
  const { section } = useNavActiveSection();
  const currentForcedActive = section === 'current';
  const pastForcedActive = section === 'past';

  // US1989116: ModernUI feedback widget. The button lives in .rightSection and
  // opens an accessible modal; submission is owned by useFeedbackSubmit.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  // Monotonic counter bumped on every successful submit. The auto-dismiss timer
  // effect depends on this nonce (not just the feedbackSuccess boolean) so that
  // back-to-back submits within the dismiss window restart the 4s timer instead
  // of being ignored because feedbackSuccess is already true.
  const [successNonce, setSuccessNonce] = useState(0);
  const { submit, submitting, error, success, reset: resetFeedback } = useFeedbackSubmit();
  const successTimerRef = useRef(null);

  // US2090203: kept as stable identities. The original reason was that FeedbackModal's
  // focus effect depended on `onClose`, so an inline arrow recreated every render made
  // that effect tear down and re-run, pulling focus out of the dialog mid-sentence. That
  // is no longer the mechanism — the same story changed the effect to depend on `isOpen`
  // alone, and it reads onClose through a ref — so this is now belt-and-braces rather
  // than load-bearing. Worth keeping: it costs nothing and it means re-adding a prop dep
  // inside the modal cannot silently reintroduce the focus-stealing loop.
  const closeFeedback = useCallback(() => setFeedbackOpen(false), []);
  // Clears any error from a previous attempt so a reopened modal does not render — and,
  // via aria-invalid/aria-describedby, announce — a stale failure against a blank form.
  const openFeedback = useCallback(() => {
    resetFeedback();
    setFeedbackOpen(true);
  }, [resetFeedback]);

  const handleFeedbackSubmit = async (payload) => {
    const ok = await submit(payload);
    // On success: close the modal and show a brief accessible confirmation.
    // On error: leave the modal open so the user sees the error and can retry
    // (behavior preserved — only the success path changes).
    if (ok) {
      setFeedbackOpen(false);
    }
  };

  // Reflect the hook's success signal into the inline confirmation. Driving
  // this from `success` (rather than a local ok flag) keeps the confirmation
  // in sync with the single source of submit truth.
  useEffect(() => {
    if (success) {
      setFeedbackSuccess(true);
      // Bump the nonce so the auto-dismiss timer effect below re-runs and
      // restarts the 4s countdown even when feedbackSuccess is already true
      // (back-to-back submits within the dismiss window).
      setSuccessNonce((n) => n + 1);
    }
  }, [success]);

  // Auto-dismiss the success confirmation (mirrors the peer-review auto-dismiss
  // pattern). Clear the timeout on unmount/re-run so we never setState after
  // unmount.
  useEffect(() => {
    if (!feedbackSuccess) return undefined;
    successTimerRef.current = setTimeout(() => {
      setFeedbackSuccess(false);
    }, 4000);
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
    // successNonce is included so every successful submit restarts the timer:
    // the cleanup clears the prior timeout and a fresh 4s countdown begins.
  }, [feedbackSuccess, successNonce]);

  return (
    <>
      {/* Row 1: Cox-branded blue bar (branding left, theme/profile right) */}
      <header className={styles.header}>
        <Container className={styles.container}>
          {/* Left: Cox Automotive Branding */}
          <div className={styles.brandingSection}>
            <a href="/" className={styles.logoLink}>
              <img
                src="/images/coxautoblue.png"
                alt="Cox Automotive"
                className={styles.logo}
              />
            </a>
          </div>

          {/* Right: Feedback + Theme Toggle + User Profile Menu */}
          <div className={styles.rightSection}>
            {user && <FeedbackButton onClick={openFeedback} />}
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
            {user && <UserProfileMenu user={user} />}
          </div>
        </Container>
      </header>

      {user && (
        <FeedbackModal
          isOpen={feedbackOpen}
          onClose={closeFeedback}
          onSubmit={handleFeedbackSubmit}
          submitting={submitting}
          error={error}
        />
      )}

      {/* Accessible success confirmation (AC#6/#8). Reuses the RefreshIndicator
          aria-live pattern; auto-dismisses via the effect above. Styled via the
          CSS module (.feedbackSuccessToast) using the same design tokens. */}
      {user && feedbackSuccess && (
        <div role="status" aria-live="polite" className={styles.feedbackSuccessToast}>
          Thanks for your feedback
        </div>
      )}

      {/* Row 2: Primary navigation tab row (persists across all Modern routes),
          styled to match the original production tab row, sits below the blue bar. */}
      {user && (
        <nav
          aria-label="Main navigation"
          className={styles.navRow}
          data-testid="main-nav"
        >
          <div className={tabStyles.tabList}>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                isActive || currentForcedActive
                  ? `${tabStyles.tab} ${tabStyles.tabActive}`
                  : tabStyles.tab
              }
              data-testid="nav-current-status-link"
            >
              Current Status
            </NavLink>
            <NavLink
              to="/past-incidents"
              end
              className={({ isActive }) =>
                isActive || pastForcedActive
                  ? `${tabStyles.tab} ${tabStyles.tabActive}`
                  : tabStyles.tab
              }
              data-testid="nav-past-incidents-link"
            >
              Past Incidents
            </NavLink>
          </div>
        </nav>
      )}
    </>
  );
};

export default ModernHeader;
